"use client";

import { useId, useState, type FormEvent } from "react";
import { VelvetRichText } from "../VelvetRichText/VelvetRichText";
import type { IFormPanelProps } from "./FormPanel.props";

const controlClass =
  "min-h-14 w-full border-0 border-b border-parchment/30 bg-transparent px-0 font-velvet-body text-xl text-foreground outline-none transition-[border-color,background-color] placeholder:text-smoke focus:border-thread focus:bg-thread/5 aria-[invalid=true]:border-error-500";

export const FormPanel = ({
  errorMessage,
  fields,
  submitLabel,
  successMessage,
}: IFormPanelProps) => {
  const baseId = useId();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submissionFailed, setSubmissionFailed] = useState(false);
  const [succeeded, setSucceeded] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const controls = Array.from(
      form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
        "input, select, textarea",
      ),
    );
    const nextErrors: Record<string, string> = {};
    for (const control of controls)
      if (!control.checkValidity()) nextErrors[control.name] = control.validationMessage;
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      controls.find((control) => !control.checkValidity())?.focus();
      return;
    }
    setSubmitting(true);
    setSubmissionFailed(false);
    try {
      if (!navigator.onLine) throw new Error("offline");
      await new Promise<void>((resolve) => window.setTimeout(resolve, 350));
      setSucceeded(true);
    } catch {
      setSubmissionFailed(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (succeeded)
    return (
      <div
        aria-live="polite"
        className="velvet-cut border border-success-500 bg-success-500/10 p-7 text-foreground md:p-10"
        role="status"
      >
        <VelvetRichText
          className="max-w-[65ch] text-primary-foreground [&_h3]:font-velvet-display [&_h3]:text-3xl [&_h3]:font-medium"
          content={successMessage}
        />
      </div>
    );

  return (
    <form className="flex flex-col gap-8" noValidate onSubmit={submit}>
      {submissionFailed ? (
        <div className="velvet-cut border border-error-500 bg-velour p-5" role="alert">
          <VelvetRichText className="text-base text-foreground" content={errorMessage} />
        </div>
      ) : null}
      {fields.map((field, index) => {
        const name = `field-${index}`;
        const inputId = `${baseId}-${index}`;
        const errorId = `${inputId}-error`;
        const error = errors[name];
        const shared = {
          "aria-describedby": error ? errorId : undefined,
          "aria-invalid": Boolean(error) as true | false,
          id: inputId,
          name,
          required: Boolean(field.required),
        };
        const label = (
          <span className="font-velvet-mono text-xs font-medium tracking-[0.03em]">
            {field.label}
            {field.required ? <span className="ml-1 text-ash">(required)</span> : null}
          </span>
        );
        return (
          <div
            className="grid gap-3 md:grid-cols-[10rem_1fr] md:items-start"
            key={`${field.label}-${index}`}
          >
            {field.fieldType === "checkbox" ? (
              <label className="flex min-h-11 cursor-pointer items-start gap-3">
                <input
                  {...shared}
                  className="size-5 shrink-0 border-graphite accent-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  type="checkbox"
                />
                {label}
              </label>
            ) : (
              <>
                {<label htmlFor={inputId}>{label}</label>}
                {field.fieldType === "long-text" ? (
                  <textarea
                    {...shared}
                    className={`${controlClass} min-h-36 resize-y py-3`}
                    placeholder={field.placeholder || undefined}
                  />
                ) : field.fieldType === "select" ? (
                  <select
                    {...shared}
                    className={controlClass}
                    defaultValue={field.placeholder ? "" : undefined}
                  >
                    {field.placeholder ? (
                      <option disabled value="">
                        {field.placeholder}
                      </option>
                    ) : null}
                    {field.selectOptions?.map((option, optionIndex) => (
                      <option key={`${option.label}-${optionIndex}`} value={option.label}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    {...shared}
                    className={controlClass}
                    pattern={field.fieldType === "phone" ? "[0-9+ .\\(\\)\\-]{7,}" : undefined}
                    placeholder={field.placeholder || undefined}
                    type={
                      field.fieldType === "email"
                        ? "email"
                        : field.fieldType === "phone"
                          ? "tel"
                          : "text"
                    }
                  />
                )}
              </>
            )}
            {error ? (
              <p className="font-velvet-mono text-xs text-error-500" id={errorId}>
                {error}
              </p>
            ) : null}
          </div>
        );
      })}
      <button
        className="velvet-cut inline-flex min-h-12 w-full items-center justify-center border border-thread bg-thread px-6 font-velvet-mono text-xs font-bold tracking-[0.08em] text-ink transition-colors hover:border-parchment hover:bg-parchment focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-wait disabled:opacity-65 sm:w-fit sm:min-w-44 md:ml-[10rem]"
        disabled={submitting}
        type="submit"
      >
        {submitting ? `${submitLabel}…` : submitLabel}
      </button>
    </form>
  );
};

FormPanel.displayName = "Content::FormPanel";
